import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { haptic } from "@/lib/haptics";

const COLORS = ["var(--ds-brand-coral)", "var(--ds-brand-sun)", "var(--ds-accent)", "var(--ds-state-success)"];
const PIECES = Array.from({ length: 18 }, (_, i) => ({
  id: i, x: (Math.random() - 0.5) * 260, y: -80 - Math.random() * 160, r: Math.random() * 540 - 270,
  c: COLORS[i % COLORS.length], d: 0.05 + Math.random() * 0.25, s: 6 + Math.random() * 8,
}));

/**
 * Célébration plein écran (commande envoyée, visite enregistrée, jalon atteint) :
 * coche animée + confettis flatline + haptique. Se ferme seule après `duration` ms.
 */
export function SuccessBurst({ show, title, subtitle, onDone, duration = 1600, confetti = true }) {
  useEffect(() => {
    if (!show) return;
    haptic.success();
    const t = setTimeout(() => onDone?.(), duration);
    return () => clearTimeout(t);
  }, [show, duration, onDone]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div data-testid="success-burst" role="status" aria-live="polite"
          className="fixed inset-0 z-[90] flex items-center justify-center pointer-events-none"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
          <div className="absolute inset-0" style={{ background: "var(--ds-bg-app)", opacity: 0.88 }} />
          {confetti && PIECES.map(p => (
            <motion.span key={p.id} aria-hidden="true" className="absolute rounded-sm"
              style={{ width: p.s, height: p.s * 0.6, background: p.c, left: "50%", top: "50%" }}
              initial={{ x: 0, y: 0, rotate: 0, opacity: 1 }}
              animate={{ x: p.x, y: p.y + 260, rotate: p.r, opacity: [1, 1, 0] }}
              transition={{ duration: 1.3, delay: p.d, ease: [0.2, 0.8, 0.3, 1] }} />
          ))}
          <motion.div className="relative flex flex-col items-center gap-3 px-8 text-center"
            initial={{ scale: 0.6, y: 20 }} animate={{ scale: 1, y: 0 }} transition={{ type: "spring", stiffness: 400, damping: 15 }}>
            <span className="w-24 h-24 rounded-full flex items-center justify-center"
              style={{ background: "var(--ds-state-success)", color: "var(--ds-state-success-solid-fg)", boxShadow: "4px 4px 0 0 var(--ds-text-primary)" }}>
              <motion.span initial={{ pathLength: 0, scale: 0.5 }} animate={{ scale: 1 }} transition={{ delay: 0.1, type: "spring", stiffness: 500, damping: 18 }}>
                <Check size={52} strokeWidth={3} aria-hidden="true" />
              </motion.span>
            </span>
            <p className="font-display text-3xl" style={{ color: "var(--ds-text-primary)" }}>{title}</p>
            {subtitle && <p className="text-base font-semibold" style={{ color: "var(--ds-text-secondary)" }}>{subtitle}</p>}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
